// Catalog.ID Client — Organization API
// ============================================================================

import { api, ApiError } from './client';
import type { RecoveryKitInfo, PutRecoveryKitRequest } from './account';
import type { KeyBagResponse } from './types';
import type {
    BeginOrgRegisterRequest, BeginOrgRegisterResponse,
    OrganizationRegisterRequest, OrganizationRegisterResponse,
    DelegateSummary,
    ClaimSummary,
    CreateClaimRequest, CreateClaimResponse,
    TerminateRequest,
} from './types';

/** Step 1: reserve org + delegate CIDs against this payment proof. */
export async function beginRegisterOrganization(req: BeginOrgRegisterRequest): Promise<BeginOrgRegisterResponse> {
    return api.post('/api/organization/register/begin', req);
}

export async function registerOrganization(req: OrganizationRegisterRequest): Promise<OrganizationRegisterResponse> {
    return api.post('/api/organization/register', req);
}

export async function listDelegates(orgCid: string): Promise<DelegateSummary[]> {
    return api.get(`/api/organization/${orgCid}/delegates`);
}

export async function revalidateDomain(orgCid: string): Promise<void> {
    return api.post(`/api/organization/${orgCid}/revalidate-domain`);
}

/**
 * Mark the ORGANIZATION account's onboarding complete (registration-restructure
 * §8.5). Owner-delegate session, idempotent, empty 200.
 *
 * The organization has no session of its own, so the founding delegate marks it
 * from the org wizard's print gate — and then marks ITSELF via
 * {@link import('./account').completeOnboarding}, since the two accounts are
 * purged independently.
 */
export async function completeOrgOnboarding(orgCid: string): Promise<void> {
    return api.post(`/api/organization/${orgCid}/onboarding/complete`);
}

export async function listOrgClaims(orgCid: string): Promise<ClaimSummary[]> {
    return api.get(`/api/organization/${orgCid}/claims`);
}

/**
 * Create a claim OWNED BY THE ORGANIZATION (owner/admin delegate session).
 *
 * Same body as the personal `claimsApi.createClaim`, but the claim's symmetric
 * key must already be wrapped to the ORGANIZATION's `attr_encrypt` public key —
 * the server stores it as the org's own "self" claim key, so a blob wrapped to
 * the delegate's key would be undecryptable by the organization it belongs to.
 * See `claimCreateTarget` in pane-types/claim-detail-pane.ts.
 */
export async function createOrgClaim(orgCid: string, req: CreateClaimRequest): Promise<CreateClaimResponse> {
    return api.post(`/api/organization/${orgCid}/claims`, req);
}

export interface RotateOrgKeysRequest {
    newAttrEncryptPublicKey: string;
    newMsgEncryptPublicKey: string;
    publicKeyAlgorithm: string;
    wrappedKeys: { delegateCid: string; keyId: string; keyIndex: number; wrappedKey: string }[];
    keyBagEntries: { keyId: string; keyIndex: number; algorithm: string; encryptedPrivateKey: string; nonce: string; status?: string }[];
    keySalt?: string;
}

export interface RotateOrgKeysResponse {
    rotated: boolean;
    claimKeysRewrapped: number;
}

export async function rotateOrgKeys(orgCid: string, req: RotateOrgKeysRequest): Promise<RotateOrgKeysResponse> {
    return api.post(`/api/organization/${orgCid}/rotate`, req);
}

/** The ORGANIZATION's encrypted key bag, same shape as `/api/account/keybag`.
 *  Owner/admin delegate sessions only — the org has no session of its own, so
 *  this is the only way to verify the org supersecret. */
export async function getOrgKeyBag(orgCid: string): Promise<KeyBagResponse> {
    return api.get(`/api/organization/${orgCid}/keybag`);
}

/** Re-encrypt the ORG key bag wholesale (finalize-at-print, registration-
 *  restructure M5). Owner-delegate session + an "org-replace-keybag" step-up
 *  challenge signed with the ORG's main_signing_key. Keys unchanged. */
export async function replaceOrgKeyBag(orgCid: string, req: {
    entries: { keyId: string; keyIndex: number; algorithm: string; encryptedPrivateKey: string; nonce: string; status?: string }[];
    keySalt?: string;
    challengeSignature: string;
}): Promise<void> {
    return api.put(`/api/organization/${orgCid}/keybag`, req);
}

export async function terminateOrganization(orgCid: string, req: TerminateRequest): Promise<void> {
    return api.post(`/api/organization/${orgCid}/terminate`, req);
}

/** The organization's standing as the node's anonymous status endpoint reports
 *  it: `active`, `locked`, `frozen` or `terminated` (docs/cid-governance.md M15). */
export async function getOrganizationStanding(orgCid: string): Promise<string> {
    const resp = await api.get<{ status: string }>(`/api/account/${orgCid}/status`, { anonymous: true });
    return resp.status;
}

/** An owner's unlock of its organization, with a step-up signed by the
 *  ORGANIZATION's main_signing_key (`org-unlock`; docs/cid-governance.md M15). */
export async function unlockOrganization(orgCid: string, challengeSignature: string): Promise<void> {
    return api.post(`/api/organization/${orgCid}/unlock`, { challengeSignature });
}

/** An owner's unfreeze of the organization bound to the node's code, signed by
 *  the organization's key (`org-unfreeze`, M15). Any other organization lifts a
 *  domain-expiry freeze by revalidating its domain. */
export async function unfreezeOrganization(orgCid: string, challengeSignature: string): Promise<void> {
    return api.post(`/api/organization/${orgCid}/unfreeze`, { challengeSignature });
}

/** Retire the organization's throwaway password at finalize, signed by the
 *  organization's key (`org-retire-password`, M15). */
export async function retireOrganizationPassword(orgCid: string, challengeSignature: string): Promise<void> {
    return api.post(`/api/organization/${orgCid}/retire-password`, { challengeSignature });
}

// ── Org chain flattening (WP §3a.3.1) ──

export interface ChainedClaimKey {
    claimUuid: string;
    encryptedKey: string;
    keyIndex: number;
    recipientType: string;
}

export interface FlattenedClaimKey {
    claimUuid: string;
    encryptedKey: string;
    keyIndex: number;
}

export interface FlattenOrgKeysRequest {
    flattenedKeys: FlattenedClaimKey[];
}

export interface FlattenOrgKeysResponse {
    flattened: number;
}

/** List the org's chained (keyIndex > 0) claim keys for the flatten work-list. */
export async function listChainedClaimKeys(orgCid: string): Promise<ChainedClaimKey[]> {
    return api.get(`/api/organization/${orgCid}/chained-claim-keys`);
}

/** Submit a batch of single-layer re-wrapped claim keys to collapse the org chain. */
export async function flattenOrgKeys(orgCid: string, req: FlattenOrgKeysRequest): Promise<FlattenOrgKeysResponse> {
    return api.post(`/api/organization/${orgCid}/flatten`, req);
}

// ── Organization Recovery Kit metadata (Recovery Kit Addendum §12/§14) ──────
// Org-scoped twins of the /api/account/recoverykit endpoints. The personal
// ones are scoped to the SESSION account — for a delegate that is the delegate,
// never the organization — so an org kit needs its own record. Delegate session
// with the owner or admin role.

/** Returns null when the organization has no kit on record. */
export async function getOrgRecoveryKit(orgCid: string): Promise<RecoveryKitInfo | null> {
    try {
        return await api.get(`/api/organization/${orgCid}/recoverykit`);
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
    }
}

export async function putOrgRecoveryKit(orgCid: string, req: PutRecoveryKitRequest): Promise<void> {
    return api.put(`/api/organization/${orgCid}/recoverykit`, req);
}

/** Record a successful org fire drill (§14). */
export async function markOrgRecoveryKitTested(orgCid: string): Promise<void> {
    return api.post(`/api/organization/${orgCid}/recoverykit/tested`, {});
}
